/**
 * The people, functionals, models and groups a search names (`author:`,
 * `uses:`, `submodel:`, `group:`): by name for the box, by id for a link's
 * labels, and by prefix for the autocomplete. The query itself only ever
 * carries ids.
 *
 *  - people: `GET api/profiles/search` (SYSTEM profiles left out);
 *  - functionals: `GET api/functionals/search`;
 *  - models (submodels, #2299): `GET api/models/search`, which lists only
 *    the models the caller may read;
 *  - groups: brewauth's `GET /groups?visibility=acl`, the groups the caller
 *    can READ, the same source as the ACL pickers.
 *
 * Ids' labels (#2301): people, functionals and models in ONE `POST api/names`
 * for them all, which names only what the caller may read (models by the
 * listings' rule) and writes no audit row; a `GET api/<type>/{id}` per id
 * wrote a READ row for each. A group's is brewauth's `GET /groups/{id}`,
 * which writes no brewlytics row.
 */
import { searchProfiles } from '@/api/profiles';
import { searchFunctionals } from '@/api/functionals';
import { searchModels } from '@/api/models';
import { brewauthGroups } from '@/api/brewauthGroups';
import { getNames, type NameKind } from '@/api/names';
import { getLabel, type LabelValue } from '@/utils/label';
import type { EntityKind, Resolution } from './searchSyntax';

export interface EntityHit {
  id: string;
  label: string;
}

const SEARCH_LIMIT = 10;

/** Up to `limit` of a kind whose name matches `text` (empty: any). */
export async function searchEntities(kind: EntityKind, text: string, limit = SEARCH_LIMIT): Promise<EntityHit[]> {
  const q = text.trim();
  if (kind === 'author') {
    const res = await searchProfiles({ text: q || '*', sort: 'metadata.label.value', skip: 0, limit });
    return (res.data || [])
      .filter((p) => !p.$isSystem)
      .map((p) => ({ id: p._id, label: getLabel(p.metadata?.label as LabelValue) || p._id }));
  }
  if (kind === 'functional') {
    const res = await searchFunctionals({ text: q || '*' });
    return (res || []).slice(0, limit).map((f) => ({ id: f._id, label: getLabel(f.metadata?.label as LabelValue) || f._id }));
  }
  if (kind === 'model') {
    const res = await searchModels(q || '*', { brief: true, limit });
    return (res.data || []).slice(0, limit).map((m) => ({ id: m._id, label: getLabel(m.metadata?.label as LabelValue) || m._id }));
  }
  const page = await brewauthGroups.listGroups({ q, visibility: 'acl', limit });
  return page.items.map((g) => ({ id: g.id, label: g.label || g.id }));
}

/**
 * What a typed name stands for: the one hit whose name is exactly it (any
 * case), else the only hit at all; several hits: ambiguous.
 */
export async function findByName(kind: EntityKind, name: string): Promise<{ resolution: Resolution; hit?: EntityHit }> {
  const hits = await searchEntities(kind, name);
  const exact = hits.filter((h) => h.label.toLowerCase() === name.toLowerCase());
  let pick: EntityHit | undefined;
  if (exact.length === 1) pick = exact[0];
  else if (exact.length === 0 && hits.length === 1) pick = hits[0];
  if (pick) return { resolution: { status: 'resolved', id: pick.id.toLowerCase() }, hit: pick };
  return { resolution: { status: exact.length > 1 || hits.length > 1 ? 'ambiguous' : 'none' } };
}

const NAME_KIND: Record<Exclude<EntityKind, 'group'>, NameKind> = {
  author: 'profiles',
  functional: 'functionals',
  model: 'models',
};

/** The key `labelsForIds` answers an id under. */
export const entityKey = (kind: EntityKind, id: string) => `${kind}\n${id.toLowerCase()}`;

/**
 * The labels of these ids, by `entityKey`: the people, functionals and models
 * in one `POST api/names`, each group from brewauth. An id that can't be read
 * has no entry; a group that can't be had leaves the others named.
 */
export async function labelsForIds(ids: ReadonlyArray<{ kind: EntityKind; id: string }>): Promise<Map<string, string>> {
  const labels = new Map<string, string>();
  const asked: Record<NameKind, string[]> = { profiles: [], functionals: [], models: [] };
  const groups: string[] = [];
  for (const { kind, id } of ids) {
    if (kind === 'group') groups.push(id);
    else asked[NAME_KIND[kind]].push(id);
  }

  const [names] = await Promise.all([
    asked.profiles.length || asked.functionals.length || asked.models.length
      ? getNames(asked).catch(() => undefined)
      : undefined,
    Promise.allSettled(groups.map(async (id) => {
      const g = await brewauthGroups.getGroup(id);
      if (g?.label) labels.set(entityKey('group', id), g.label);
    })),
  ]);
  for (const [kind, nameKind] of Object.entries(NAME_KIND) as [EntityKind, NameKind][]) {
    for (const [id, label] of Object.entries(names?.[nameKind] ?? {})) {
      if (label) labels.set(entityKey(kind, id), label);
    }
  }
  return labels;
}
